import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { useTranslation } from "@nova/use-translation"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useCallback, useEffect, useState } from "react"
import { selectUserID } from "user/profile/selectors"
import { Licenses } from "@nova/constants"
import { updateLicense } from "features/entity-caching/licenses/thunks"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { NovaSpinner } from "@nova/component-nova-spinner"
import { ScrollableArea } from "@nova/component-scrollable-area"
import * as Icons from "@nova/component-icons"
import { selectLicenseUpgrade } from "features/entity-caching/licenses/selectors"
import { hideGlobalModal } from "runlevel/actions"

type Invoice = {
    subtotal: number
    total: number
    tax: number
    breakdown: { amount: number; description: string }[]
    cardNumber: string
}

function formatDollarAmount(amount: number): string {
    const sign = amount < 0 ? "-" : ""
    const absoluteAmount = Math.abs(amount)

    const cents = absoluteAmount % 100
    const dollars = Math.floor(absoluteAmount / 100)

    const dollarString = dollars.toLocaleString("en-US")
    const centString = cents.toString().padStart(2, "0")
    return `${sign}$${dollarString}.${centString}`
}

export const LicenseUpgradeModal = () => {
    const { t } = useTranslation()

    const dispatch = useAppDispatch()

    const userId = useAppSelector(selectUserID)

    const license = useAppSelector(selectLicenseUpgrade())

    const [invoiceData, setInvoiceData] = useState<Invoice | undefined>(undefined)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        const fetchInvoice = async () => {
            if (!license) return
            const res = await fetch(
                `/api/stripe/stripe-preview-proration?subscriptionId=${license.subscriptionId}&billingCycle=${license.billingCycle}`
            ).then((r) => r.json())
            setInvoiceData(res)
        }
        fetchInvoice()
    }, [license])

    const upgradeLicense = useCallback(async () => {
        if (!license) return
        const { ownerId, licenseId, subscriptionId, billingCycle } = license
        if (ownerId !== userId) {
            dispatch(
                setGlobalNotificationContext({
                    status: GlobalNotificationStatus.error,
                    message: "Failed to upgrade license. Contact your Billing Admin to upgrade.",
                    timeoutInMs: 8000,
                })
            )
            dispatch(hideGlobalModal())
            return
        }
        if (!subscriptionId) {
            dispatch(
                setGlobalNotificationContext({
                    status: GlobalNotificationStatus.error,
                    message: "Sorry, we can't upgrade your License. Please contact Sales.",
                    timeoutInMs: 8000,
                })
            )
            dispatch(hideGlobalModal())
            return
        }
        setIsLoading(true)
        const res = await fetch(
            `/api/stripe/stripe-upgrade-license?subscriptionId=${subscriptionId}&billingCycle=${billingCycle}`
        ).then((r) => r.json())
        setIsLoading(false)
        if (!res.received) {
            dispatch(
                setGlobalNotificationContext({
                    status: GlobalNotificationStatus.error,
                    message: "Failed to upgrade license. Please contact support.",
                    timeoutInMs: 8000,
                })
            )
            dispatch(hideGlobalModal())
            return
        }
        dispatch(
            updateLicense({
                licenseId,
                ownerId,
                attributes: { subscriptionType: Licenses.Pro, createdAt: new Date().toISOString() },
            })
        )
        dispatch(
            setGlobalNotificationContext({
                status: GlobalNotificationStatus.success,
                message:
                    "Upgrade complete. A confirmation email has\nbeen sent to you for your records.",
                timeoutInMs: 8000,
            })
        )
        dispatch(hideGlobalModal())
    }, [userId, license, dispatch])

    return (
        <HighPriorityNovaModalContainer
            title="Upgrade license"
            icon={Icons.PersonUpgrade}
            closeModal={() => dispatch(hideGlobalModal())}
            className="w-[34.375rem]"
            bottomBar={
                <div className="w-full flex justify-end items-center gap-2">
                    <ButtonTertiary title="Cancel" onClick={() => dispatch(hideGlobalModal())} />
                    <ButtonPrimary
                        title={"Pay Now"}
                        onClick={upgradeLicense}
                        LeftIcon={Icons.Upgrade}
                        isLoading={isLoading}
                        disabled={!invoiceData}
                    />
                </div>
            }
        >
            {!invoiceData && (
                <div className="flex justify-center items-center !h-[25rem] ">
                    <NovaSpinner width={32} height={32} />
                </div>
            )}
            {invoiceData && (
                <>
                    <h5 className="mb-4">
                        {t("Upgrade to a Pro License for unlimited data storage.")}
                    </h5>
                    <h4 className="mb-1">{t("Cost breakdown:")}</h4>
                    <ScrollableArea className="flex flex-col gap-1 max-h-[12.5rem]">
                        {invoiceData?.breakdown.map((b, idx) => (
                            <div key={idx} className="flex justify-between gap-8">
                                <h5 className=" text-gray-300">{t(b.description)}</h5>
                                <h5 className="mono">{t(formatDollarAmount(b.amount))}</h5>
                            </div>
                        ))}
                    </ScrollableArea>
                    <div className="w-full border-b border-gray-700 my-2" />
                    <div className="flex justify-between">
                        <h5 className="mb-1">{t("Subtotal")}</h5>
                        <h5 className="mono">{t(formatDollarAmount(invoiceData?.subtotal))}</h5>
                    </div>
                    <div className="flex justify-between">
                        <h5 className="mb-1 text-gray-300">{t("Taxes")}</h5>
                        <h5 className="mono">{t(formatDollarAmount(invoiceData?.tax))}</h5>
                    </div>
                    <div className="w-full border-b border-gray-700 my-2" />
                    <div className="flex justify-between">
                        <h5 className="mb-1">{t("Total")}</h5>
                        <h5 className="mono">{t(formatDollarAmount(invoiceData?.total))}</h5>
                    </div>
                    <div className="flex justify-between mb-4">
                        <h5 className=" text-gray-300">
                            {t(`using the credit card ending in **${invoiceData.cardNumber}`)}
                        </h5>
                        <small className="text-gray-400">{t("USD")}</small>
                    </div>
                    <h5 className="text-gray-50">
                        {t("Need help or exploring manned aircraft integrations?")}
                    </h5>
                    <h5
                        className="text-blue-500 underline cursor-pointer"
                        onClick={() => window.open("mailto:help@mapnova.com", "_blank")}
                    >
                        {t("Contact sales.")}
                    </h5>
                </>
            )}
        </HighPriorityNovaModalContainer>
    )
}
